import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'

const CLUBS = [
  { key: 'dreamers',       label: 'Dreamers Club',       emoji: '🎭', threshold: 0   },
  { key: 'budding_star',   label: 'Budding Star Club',   emoji: '⭐', threshold: 5   },
  { key: 'debt_crusher',   label: 'Debt Crusher Club',   emoji: '💪', threshold: 15  },
  { key: 'shining_star',   label: 'Shining Star Club',   emoji: '✨', threshold: 30  },
  { key: 'achievers',      label: "Achiever's Club",     emoji: '🏆', threshold: 50  },
  { key: 'game_changer',   label: 'Game Changer Club',   emoji: '🎯', threshold: 65  },
  { key: 'wealth_builder', label: 'Wealth Builder Club', emoji: '💰', threshold: 80  },
  { key: 'visionaries',    label: 'Visionaries Club',    emoji: '🔮', threshold: 85  },
  { key: 'luminaries',     label: 'Luminaries Club',     emoji: '💡', threshold: 90  },
  { key: 'trailblazers',   label: 'Trailblazers Club',   emoji: '🔥', threshold: 93  },
  { key: 'pioneers',       label: 'Pioneers Club',       emoji: '🚀', threshold: 96  },
  { key: 'tycoons',        label: 'Tycoons Club',        emoji: '👑', threshold: 100 },
]

function getCurrentClub(pct) {
  let club = CLUBS[0]
  for (const c of CLUBS) {
    if (pct >= c.threshold) club = c
    else break
  }
  return club
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true); return
    }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export default function MembershipCard({ session, onUpgradeSuccess }) {
  const [isPaid, setIsPaid]             = useState(false)
  const [userRole, setUserRole]         = useState('user')
  const [giftExpiresAt, setGiftExpiresAt] = useState(null)
  const [loans, setLoans]               = useState([])
  const [firstName, setFirstName]       = useState('')
  const [loading, setLoading]           = useState(true)
  const [paymentStatus, setPaymentStatus] = useState(null)

  const fetchMembership = useCallback(async () => {
    const [{ data: mem }, { data: prof }] = await Promise.all([
      supabase.from('memberships')
        .select('is_paid, gift_expires_at')
        .eq('user_id', session.user.id)
        .single(),
      supabase.from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single(),
    ])
    setIsPaid(!!mem?.is_paid)
    setUserRole(prof?.role || 'user')
    setGiftExpiresAt(mem?.gift_expires_at || null)
  }, [session?.user?.id])

  useEffect(() => {
    if (!session?.user?.id) return
    async function fetchAll() {
      const [{ data: loanData }, { data: profileData }] = await Promise.all([
        supabase.from('loans').select('joining_balance, balance').eq('user_id', session.user.id),
        supabase.from('profiles').select('full_name').eq('id', session.user.id).single(),
      ])
      if (loanData) setLoans(loanData)
      if (profileData?.full_name) setFirstName(profileData.full_name.trim().split(' ')[0])
    }
    Promise.all([fetchMembership(), fetchAll()]).finally(() => setLoading(false))
  }, [session?.user?.id, fetchMembership])

  const totalJoining = loans.reduce((s, l) => s + parseFloat(l.joining_balance || l.balance || 0), 0)
  const totalBalance  = loans.reduce((s, l) => s + parseFloat(l.balance || 0), 0)
  const debtReductionPct = totalJoining > 0
    ? Math.min(100, Math.max(0, Math.round(((totalJoining - totalBalance) / totalJoining) * 100)))
    : 0

  const currentClub = getCurrentClub(debtReductionPct)
  const nextClubIdx = CLUBS.findIndex(c => c.key === currentClub.key) + 1
  const nextClub    = nextClubIdx < CLUBS.length ? CLUBS[nextClubIdx] : null

  const progressPct = nextClub
    ? Math.min(100, Math.max(0, Math.round(
        ((debtReductionPct - currentClub.threshold) / (nextClub.threshold - currentClub.threshold)) * 100
      )))
    : 100
  const progressColor = progressPct >= 75 ? '#4a6741' : progressPct >= 40 ? '#c9a84c' : '#b8441a'

  const isGifted    = userRole === 'gifted'
  const effectivePaid = isPaid || isGifted

  async function handleUpgrade() {
    setPaymentStatus({ type: 'processing', message: 'Initializing payment…' })
    const scriptLoaded = await loadRazorpayScript()
    if (!scriptLoaded) {
      setPaymentStatus({ type: 'error', message: 'Failed to load payment gateway. Please check your connection.' })
      return
    }
    let orderData
    try {
      const res = await fetch('/.netlify/functions/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_in_paise: 14900, tier_name: 'premium' }),
      })
      orderData = await res.json()
      if (!res.ok) {
        setPaymentStatus({ type: 'error', message: orderData.error || 'Failed to create order. Please try again.' })
        return
      }
    } catch {
      setPaymentStatus({ type: 'error', message: 'Network error. Please check your connection.' })
      return
    }
    setPaymentStatus(null)
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'DebtCrush',
      description: 'DebtCrush Premium Membership — ₹149/month',
      order_id: orderData.order_id,
      handler: async (response) => {
        setPaymentStatus({ type: 'processing', message: 'Verifying payment…' })
        try {
          const verifyRes = await fetch('/.netlify/functions/verify-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              user_id: session.user.id,
              tier: 'premium',
              amount: 14900,
            }),
          })
          const result = await verifyRes.json()
          if (verifyRes.ok && result.verified) {
            setIsPaid(true)
            setPaymentStatus({ type: 'success', message: 'Welcome to DebtCrush Premium! Your membership is now active.' })
            onUpgradeSuccess?.()
          } else {
            setPaymentStatus({ type: 'error', message: 'Payment verification failed. Contact support if money was deducted.' })
          }
        } catch {
          setPaymentStatus({ type: 'error', message: 'Could not verify payment. Please contact support.' })
        }
      },
      modal: { ondismiss: () => setPaymentStatus({ type: 'error', message: 'Payment cancelled. No amount was charged.' }) },
      prefill: { email: session.user.email },
      theme: { color: '#c9a84c' },
    }
    const rzp = new window.Razorpay(options)
    rzp.on('payment.failed', (response) => {
      setPaymentStatus({ type: 'error', message: response.error?.description || 'Payment failed. Please try again.' })
    })
    rzp.open()
  }

  if (loading) return (
    <div style={{ background: '#0d0d0d', borderRadius: '8px', padding: '1.3rem 1.5rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#c9a84c' }} />
      <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#ede8dc' }}>Loading membership…</div>
    </div>
  )

  const isProcessing = paymentStatus?.type === 'processing'

  const statusBanner = paymentStatus && (
    <div style={{
      background: paymentStatus.type === 'success' ? 'rgba(74,103,65,0.12)' : paymentStatus.type === 'error' ? 'rgba(184,68,26,0.08)' : 'rgba(201,168,76,0.08)',
      border: `1px solid ${paymentStatus.type === 'success' ? 'rgba(74,103,65,0.4)' : paymentStatus.type === 'error' ? 'rgba(184,68,26,0.35)' : 'rgba(201,168,76,0.35)'}`,
      borderRadius: '6px', padding: '0.7rem 1rem', marginBottom: '1rem',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
    }}>
      <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600',
        color: paymentStatus.type === 'success' ? '#4a6741' : paymentStatus.type === 'error' ? '#b8441a' : '#c9a84c' }}>
        {paymentStatus.type === 'processing' && '⏳ '}
        {paymentStatus.type === 'success'    && '✓ '}
        {paymentStatus.type === 'error'      && '✗ '}
        {paymentStatus.message}
      </div>
      {paymentStatus.type !== 'processing' && (
        <button onClick={() => setPaymentStatus(null)} style={{ background: 'none', border: 'none', color: '#9a8f82', cursor: 'pointer', fontSize: '0.8rem', padding: '0 0.3rem' }}>✕</button>
      )}
    </div>
  )

  return (
    <div style={{ background: '#0d0d0d', border: '1px solid #2a2520', borderRadius: '8px', padding: '1.15rem 1.35rem', marginBottom: '1.25rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#c9a84c' }} />

      {statusBanner}

      {/* ── CLUB SECTION ── */}
      <div style={{ marginBottom: '1.1rem' }}>
        <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9a8f82', marginBottom: '0.4rem' }}>
          Your Club · earned by performance
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.7rem' }}>
          <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{currentClub.emoji}</span>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.3rem', fontWeight: '700', color: '#f5c518', lineHeight: 1 }}>
            {currentClub.label}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#9a8f82' }}>
            Debt reduced: <span style={{ color: '#f5c518', fontWeight: '700' }}>{debtReductionPct}%</span>
            {nextClub
              ? <> · toward {nextClub.emoji} {nextClub.label} at {nextClub.threshold}%</>
              : ' · Maximum club reached! 🎉'}
          </span>
        </div>
        <div style={{ height: '6px', background: '#252118', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: progressPct + '%', background: progressColor, borderRadius: '99px', transition: 'width 0.8s ease' }} />
        </div>
        {nextClub && (
          <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#9a8f82', marginTop: '0.3rem', textAlign: 'right' }}>
            {nextClub.threshold - debtReductionPct}% more to reach {nextClub.emoji} {nextClub.label}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #2a2520', marginBottom: '1rem' }} />

      {/* ── MEMBERSHIP SECTION ── */}
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9a8f82', marginBottom: '0.5rem' }}>
          Membership
        </div>

        {isGifted ? (
          /* GIFTED USER */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem' }}>
            <div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: '700', color: '#f5c518' }}>
                🎁 Gifted Premium
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#9a8f82', marginTop: '0.15rem' }}>
                Unlimited loans · Smart Payoff · PDF Export · More
              </div>
              {giftExpiresAt && (
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(201,168,76,0.7)', marginTop: '0.25rem' }}>
                  Valid until: {new Date(giftExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
            <div style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', padding: '0.4rem 0.75rem', fontFamily: 'monospace', fontSize: '0.68rem', color: '#c9a84c', fontWeight: '600', whiteSpace: 'nowrap' }}>
              🎁 Gifted
            </div>
          </div>
        ) : effectivePaid ? (
          /* PAID USER */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem' }}>
            <div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: '700', color: '#f5c518' }}>
                ✦ DebtCrush Premium
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#9a8f82', marginTop: '0.15rem' }}>
                Unlimited loans · Smart Payoff · PDF Export · Alerts
              </div>
            </div>
            <div style={{ background: 'rgba(74,103,65,0.12)', border: '1px solid rgba(74,103,65,0.35)', borderRadius: '6px', padding: '0.4rem 0.75rem', fontFamily: 'monospace', fontSize: '0.68rem', color: '#4a6741', fontWeight: '600', whiteSpace: 'nowrap' }}>
              ✓ Active
            </div>
          </div>
        ) : (
          /* FREE USER */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.05rem', fontWeight: '700', color: '#ede8dc' }}>
                Free Member
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#9a8f82', marginTop: '0.15rem' }}>
                {firstName ? `${firstName}, upgrade` : 'Upgrade'} to unlock unlimited loans, Smart Payoff Engine, PDF Export &amp; more
              </div>
            </div>
            <button
              disabled={isProcessing}
              onClick={handleUpgrade}
              style={{
                background: isProcessing ? '#5a4f3a' : '#c9a84c',
                color: '#0d0d0d', border: 'none', borderRadius: '5px',
                padding: '0.5rem 1rem', fontFamily: 'monospace',
                fontSize: '0.72rem', fontWeight: '700',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap', opacity: isProcessing ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (!isProcessing) e.currentTarget.style.opacity = '0.88' }}
              onMouseLeave={e => { if (!isProcessing) e.currentTarget.style.opacity = '1' }}
            >
              {isProcessing ? '⏳ Processing…' : 'Upgrade — ₹149/month'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
